import React, { useState } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../context/AuthContext'
import { useDTEsReserva, useDTEsPago, useCrearDTE, useActualizarDTE, useAnularDTE } from '../../hooks/useReservasManual'
import { DocumentoTributario } from '../../hooks/useReservasManual'

interface DTEModalManagerProps {
  isOpen: boolean
  onClose: () => void
  reservaId: string | null
  pagoId: number | null
  monto?: number // Monto sugerido (opcional)
  onDTECreated?: () => void
}

export const DTEModalManager: React.FC<DTEModalManagerProps> = ({
  isOpen,
  onClose,
  reservaId,
  pagoId,
  monto,
  onDTECreated,
}) => {
  const { colors } = useTheme()
  const { tienePermiso } = useAuth()
  const [tab, setTab] = useState<'list' | 'create'>('list')
  const [formData, setFormData] = useState({
    tipo_documento: 'boleta',
    numero_dte: '',
    folio_interno: '',
    monto_dte: monto || 0, // Monto del DTE (editable)
    fecha_vencimiento: '',
  })

  // Obtener DTEs de la reserva
  const { data: allDtes = [], isLoading: loadingDTEs } = useDTEsReserva(reservaId)

  // Filtrar DTEs por pago si pagoId está disponible
  const dtes = pagoId
    ? (allDtes as any[]).filter((dte: any) => dte.pago_id === pagoId)
    : (allDtes as any[])

  const crearDTE = useCrearDTE()
  const actualizarDTE = useActualizarDTE()
  const anularDTE = useAnularDTE()

  if (!isOpen) return null

  const handleCrearDTE = async () => {
    if (!reservaId || !pagoId || !formData.numero_dte || formData.monto_dte <= 0) {
      alert('Completa los campos requeridos (monto debe ser mayor a 0)')
      return
    }

    try {
      const payload: any = {
        pago_id: pagoId,
        reserva_id: reservaId,
        tipo_documento: formData.tipo_documento,
        numero_dte: formData.numero_dte,
        monto: formData.monto_dte, // Usar monto editable del formulario
        proveedor_dte: 'sii_manual',
        status_dte: 'emitido',
      }

      // Solo agregar campos opcionales si tienen valor
      if (formData.folio_interno && formData.folio_interno.trim()) {
        payload.folio_interno = formData.folio_interno.trim()
      }

      if (formData.fecha_vencimiento && formData.fecha_vencimiento.trim()) {
        payload.fecha_vencimiento = formData.fecha_vencimiento
      }

      await crearDTE.mutateAsync(payload)

      setFormData({
        tipo_documento: 'boleta',
        numero_dte: '',
        folio_interno: '',
        monto_dte: monto || 0,
        fecha_vencimiento: '',
      })

      setTab('list')
      onDTECreated?.()
    } catch (e) {
      console.error('Error creando DTE:', e)
    }
  }

  const handleAnularDTE = async (dteId: string) => {
    if (!confirm('¿Está seguro de anular este DTE?')) return

    try {
      await anularDTE.mutateAsync(dteId)
    } catch (e) {
      console.error('Error anulando DTE:', e)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        style={{ maxHeight: '80vh' }}
      >
        {/* Header */}
        <div
          className="text-white px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ backgroundColor: colors.primary }}
        >
          <h2 className="text-xl font-bold">Gestión de Documentos Tributarios (DTEs)</h2>
          <button
            onClick={onClose}
            className="text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${colors.primaryDark}60`)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            ✕
          </button>
        </div>

        {/* Información del Pago (solo cuando se abre desde un pago) */}
        {pagoId && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600">Pago ID:</span>
                <span className="ml-2 font-semibold text-gray-900">#{pagoId}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Monto:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  ${monto?.toLocaleString('es-CL')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 pt-4 flex-shrink-0">
          <button
            onClick={() => setTab('list')}
            className={`px-4 py-2 font-medium transition-colors ${
              tab === 'list'
                ? 'border-b-2 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            style={{
              borderColor: tab === 'list' ? colors.primary : 'transparent',
              color: tab === 'list' ? colors.primary : undefined,
            }}
          >
            📋 DTEs Registrados ({dtes.length})
          </button>
          {tienePermiso('dtes.crear') && (
            <button
              onClick={() => setTab('create')}
              className={`px-4 py-2 font-medium transition-colors ${
                tab === 'create'
                  ? 'border-b-2 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{
                borderColor: tab === 'create' ? colors.primary : 'transparent',
                color: tab === 'create' ? colors.primary : undefined,
              }}
            >
              ➕ Nuevo DTE
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'list' ? (
            <div className="p-6">
              {loadingDTEs ? (
                <div className="text-center py-8">Cargando DTEs...</div>
              ) : dtes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>
                    {pagoId
                      ? 'No hay DTEs registrados para este pago'
                      : 'No hay DTEs registrados para esta reserva'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dtes.map((dte) => (
                    <div
                      key={dte.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">
                            {dte.tipo_documento.toUpperCase()} - {dte.numero_dte}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Emitido: {new Date(dte.fecha_emision).toLocaleDateString('es-CL')}
                          </p>
                        </div>
                        <div
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: `${colors.primary}15`,
                            color: colors.primary,
                          }}
                        >
                          {dte.status_dte}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Monto</p>
                          <p className="font-semibold text-gray-900">
                            ${dte.monto.toLocaleString('es-CL')}
                          </p>
                        </div>
                        {dte.fecha_vencimiento && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Vencimiento</p>
                            <p className="font-semibold text-gray-900">
                              {new Date(dte.fecha_vencimiento).toLocaleDateString('es-CL')}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {dte.url_documento && (
                          <a
                            href={dte.url_documento}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 px-3 py-2 text-xs rounded font-semibold text-center transition-all"
                            style={{
                              color: colors.primary,
                              backgroundColor: `${colors.primary}15`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.primary}25`
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.primary}15`
                            }}
                          >
                            📥 Descargar PDF
                          </a>
                        )}
                        {dte.status_dte !== 'anulado' && (
                          <button
                            onClick={() => handleAnularDTE(dte.id)}
                            className="flex-1 px-3 py-2 text-xs rounded font-semibold transition-all"
                            style={{
                              color: colors.error,
                              backgroundColor: `${colors.error}15`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.error}25`
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.error}15`
                            }}
                          >
                            Anular
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-4">
                {/* Tipo de documento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Documento *
                  </label>
                  <select
                    value={formData.tipo_documento}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo_documento: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-2 focus:border-transparent"
                  >
                    <option value="boleta">Boleta</option>
                    <option value="factura">Factura</option>
                    <option value="nota_credito">Nota de Crédito</option>
                  </select>
                </div>

                {/* Número DTE */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número DTE *
                  </label>
                  <input
                    type="text"
                    value={formData.numero_dte}
                    onChange={(e) =>
                      setFormData({ ...formData, numero_dte: e.target.value })
                    }
                    placeholder="Ej: BL-0001234"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-2 focus:border-transparent"
                  />
                </div>

                {/* Folio interno */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Folio Interno
                  </label>
                  <input
                    type="text"
                    value={formData.folio_interno}
                    onChange={(e) =>
                      setFormData({ ...formData, folio_interno: e.target.value })
                    }
                    placeholder="Folio interno del sistema"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-2 focus:border-transparent"
                  />
                </div>

                {/* Fecha vencimiento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Vencimiento (para facturas)
                  </label>
                  <input
                    type="date"
                    value={formData.fecha_vencimiento}
                    onChange={(e) =>
                      setFormData({ ...formData, fecha_vencimiento: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-2 focus:border-transparent"
                  />
                </div>

                {/* Monto (editable) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto DTE *
                  </label>
                  <input
                    type="number"
                    value={formData.monto_dte}
                    onChange={(e) =>
                      setFormData({ ...formData, monto_dte: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="Monto del documento"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-2 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {monto ? `Monto del pago: $${monto.toLocaleString('es-CL')}` : 'No hay monto sugerido'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg font-semibold transition-all"
            style={{
              backgroundColor: '#f3f4f6',
              color: colors.primary,
              border: `1px solid #d1d5db`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6'
            }}
          >
            Cerrar
          </button>
          {tab === 'create' && (
            <button
              onClick={handleCrearDTE}
              disabled={crearDTE.isPending}
              className="px-4 py-2 text-sm rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: colors.primary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
            >
              {crearDTE.isPending ? '⏳ Guardando...' : '✓ Guardar DTE'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DTEModalManager
